import { CheckIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';
import type { Package } from '@/types/subscription';
import { BILLING_PERIOD_MONTHS } from '@/types/subscription';

const NZD_USD_RATE = 0.60;

export interface PricingCardProps {
  pkg: Package;
  currency: 'NZD' | 'USD';
  delay?: number;
  disableScale?: boolean;
  lowerPlanName?: string;
  onSelect: (slug: string) => void;
  isCurrentPlan?: boolean;
  selectLabel?: string;
}

function formatPrice(cents: number, currency: 'NZD' | 'USD'): string {
  const dollars = currency === 'USD' ? (cents / 100) * NZD_USD_RATE : cents / 100;
  return dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2);
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(0)}K`;
  return tokens.toString();
}

export function PricingCard({
  pkg,
  currency,
  delay = 0,
  disableScale = false,
  lowerPlanName,
  onSelect,
  isCurrentPlan = false,
  selectLabel,
}: PricingCardProps) {
  const quarterlyPrice = pkg.monthly_price_cents * BILLING_PERIOD_MONTHS;
  const isPayAsYouGo = pkg.included_tokens === 0;
  const monthlyEquivalent = !isPayAsYouGo && pkg.monthly_price_cents > 0
    ? `$${formatPrice(pkg.monthly_price_cents, currency)}/mo`
    : null;
  const periodLabel = isPayAsYouGo ? '/1K tokens' : '/quarter';
  const priceDisplay = isPayAsYouGo
    ? formatPrice(pkg.cost_per_1000_tokens_cents, currency)
    : formatPrice(quarterlyPrice, currency);
  const tokenAllocation = isPayAsYouGo
    ? 'Pay-as-you-go'
    : `${formatTokens(pkg.included_tokens)} tokens/month`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className={`relative flex flex-col rounded-xl bg-slate-800 p-6 sm:p-8 ${
        pkg.is_popular
          ? `border-2 border-cyan-400 shadow-[0_0_30px_rgba(34,211,238,0.15)] z-10 ${disableScale ? '' : 'md:scale-105'}`
          : 'border border-slate-700'
      }`}
    >
      {pkg.is_popular && (
        <div className="absolute -top-4 left-0 right-0 mx-auto w-fit rounded-full bg-cyan-400 px-3 py-1 text-sm font-bold uppercase tracking-wider text-slate-900">
          Most Popular
        </div>
      )}

      <div className="mb-6">
        <h3
          className={`text-sm font-bold uppercase tracking-wider ${
            pkg.is_popular ? 'text-cyan-400' : 'text-white'
          }`}
        >
          {pkg.name}
        </h3>
        <div className="mt-4 flex items-baseline text-white">
          <span className="text-3xl sm:text-4xl font-bold tracking-tight">
            ${priceDisplay}
          </span>
          <span className="ml-1 text-sm font-medium text-slate-400">
            {periodLabel}
          </span>
        </div>
        <div className="mt-1 h-5 text-sm text-slate-400">
          {monthlyEquivalent && <span>{monthlyEquivalent}</span>}
        </div>
      </div>

      <div className="mb-6 inline-flex w-fit items-center rounded-md bg-slate-900/50 px-3 py-1.5 text-sm font-medium text-slate-300 border border-slate-700">
        {tokenAllocation}
      </div>

      <div className="mb-8 h-px w-full bg-slate-700" />

      <ul className="mb-8 flex-1 space-y-4">
        {lowerPlanName && !pkg.features.some((f) => f.toLowerCase().includes(`all ${lowerPlanName.toLowerCase()}`)) && (
          <li className="flex items-start">
            <CheckIcon className="mr-3 h-5 w-5 shrink-0 text-cyan-400" />
            <span className="text-sm text-slate-300 leading-tight pt-0.5 font-medium">
              All {lowerPlanName} features
            </span>
          </li>
        )}
        {pkg.features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <CheckIcon className="mr-3 h-5 w-5 shrink-0 text-cyan-400" />
            <span className="text-sm text-slate-300 leading-tight pt-0.5">
              {feature}
            </span>
          </li>
        ))}
      </ul>

      {isCurrentPlan ? (
        <div className="mt-auto w-full rounded-lg px-4 py-3 text-sm font-semibold text-center border border-emerald-500/50 bg-emerald-500/10 text-emerald-400">
          Current Plan
        </div>
      ) : (
        <button
          onClick={() => onSelect(pkg.slug)}
          className={`mt-auto w-full rounded-lg px-4 py-3 text-sm font-semibold transition-all duration-200 ${
            pkg.is_popular
              ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg shadow-blue-500/25'
              : 'border border-slate-700 bg-transparent text-white hover:bg-slate-700 hover:border-slate-500'
          }`}
        >
          {selectLabel ?? 'Get Started'}
        </button>
      )}
    </motion.div>
  );
}
